import { useVideoCached } from '@/features/shared/Hooks/useVideoCached';
import { SixteenNineIcon } from '@/features/shared/Icons';
import Image from '@/features/shared/Image';
import { isAutoplayVideoAllowed } from '@/utils/helpers';
import clsx from 'clsx';
import React, {
  memo,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import YouTube, { Options as YoutubeOptions } from 'react-youtube';
import { VideoProps } from './Video.interface';
import useStyles from './Video.styles';
import { isNoscriptVideoBlocked } from './Video.utils';

interface EmbedPlayerProps {
  classes?: any;
  videoUrl?: string;
  isVisible?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  onVideoPlayed?: any;
}

const CustomVideo = memo(
  ({
    classes,
    videoUrl,
    isVisible = false,
    onVideoPlayed = () => {},
  }: EmbedPlayerProps): JSX.Element => {
    const video = useRef<HTMLVideoElement>(null);
    const playVideo = () => {
      const playPromise = video.current?.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('video played auto');
            onVideoPlayed();
          })
          .catch(() => {
            console.log('playback prevented');
          });
      }
    };

    const pauseVideo = () => {
      video.current?.pause();
      console.log('video paused');
    };

    const loadVideo = () => {
      const source = video.current?.firstChild as HTMLSourceElement;

      if (source?.hasAttribute('data-src')) {
        if (isVisible) {
          if (source) {
            source.src = source?.getAttribute('data-src') || '';
            source?.removeAttribute('data-src');
            video.current?.load();
          }
        }

        return true;
      }

      return false;
    };

    useEffect(() => {
      (async () => {
        const loading = await loadVideo();

        if (loading) return;

        if (isVisible) {
          playVideo();
        } else {
          pauseVideo();
        }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    useEffect(() => {
      if (isVisible) {
        loadVideo();
      } else {
        pauseVideo();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoUrl]);

    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        ref={video}
        loop
        autoPlay={isAutoplayVideoAllowed}
        muted
        playsInline
        className={classes.customVideo}
        tabIndex={-1}
      >
        <source data-src={videoUrl} />
      </video>
    );
  },
);

const YoutubeVideo = memo(
  ({ classes, videoUrl, isVisible = false }: EmbedPlayerProps): JSX.Element => {
    const [video, setVideo] = useState<any>(null);

    const youtubeId = useMemo(() => {
      // eslint-disable-next-line no-useless-escape
      const REGEX_GET_YOUTUBE_ID =
        /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
      const MATCH = videoUrl && videoUrl.match(REGEX_GET_YOUTUBE_ID);
      return MATCH && MATCH[7].length === 11 ? MATCH[7] : '';
    }, [videoUrl]);

    const restartVideo = (event: any) => {
      event.target.seekTo(0);
    };

    useEffect(() => {
      if (isVisible) {
        video?.playVideo();
      } else {
        video?.pauseVideo();
      }
    }, [isVisible, video]);

    const videoOpts: YoutubeOptions = {
      playerVars: {
        rel: 0,
        modestbranding: 1,
        iv_load_policy: 3,
        autoplay: 1,
        playsinline: 1,
        mute: 1,
        loop: 1,
        controls: 0,
        showinfo: 1,
      },
    };

    return videoUrl ? (
      <YouTube
        onReady={(event: any) => setVideo(event.target)}
        data-testid="qa-video-component-iframe"
        className={classes.youtubeVideo}
        videoId={youtubeId}
        opts={videoOpts}
        onEnd={restartVideo}
      />
    ) : (
      <div>Youtube ID is not valid</div>
    );
  },
);

const VideoBackgroundComponent = ({
  videoUrl = '',
  className,
  fallbackImage = {},
}: PropsWithChildren<VideoProps>) => {
  const [orientation, setOrientation] = useState<'vertical' | 'horizontal'>('horizontal');
  const containerRef = useRef<HTMLDivElement>(null);
  const classes = useStyles({});
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPlayed, setIsVideoPlayed] = React.useState<boolean>(false);
  const [isVideoBlocked, setIsVideoBlocked] = React.useState<boolean>(true);
  const onChange = (visible: boolean) => {
    setIsVisible(visible);
  };

  const updateOrientation = useCallback(() => {
    if (containerRef?.current?.parentElement) {
      // eslint-disable-next-line prefer-const
      let { clientWidth: w, clientHeight: h } = containerRef.current.parentElement;
      if (w === 0) {
        w = containerRef.current.parentElement.parentElement
          ? containerRef.current.parentElement.parentElement.clientWidth
          : window.innerWidth;
      }
      setOrientation(w > h && w / h >= 16 / 9 ? 'horizontal' : 'vertical');
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', updateOrientation, { passive: true });

    return () => window.removeEventListener('resize', updateOrientation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateOrientation();
  }, [updateOrientation, videoUrl]);

  const isCustomVideo = React.useMemo(() => {
    const IS_YOUTUBE = /youtu/i.test(videoUrl);
    const IS_YOUTUBE_ID = videoUrl.length === 11;
    const IS_CUSTOM_VIDEO = !IS_YOUTUBE && !IS_YOUTUBE_ID;
    return IS_CUSTOM_VIDEO;
  }, [videoUrl]);

  const VideoVariant = React.useMemo(
    () => (isCustomVideo ? CustomVideo : YoutubeVideo),
    [isCustomVideo],
  );
  const bgVideoClassName = clsx(classes.bgVideo, `${orientation}-orientation`);
  const sixteenNineClassName = clsx(classes.sixteenNine, `${orientation}-orientation`);
  const [isVideoCached] = useVideoCached({ videoUrl, isCustomVideo, isVideoPlayed });
  const isFallbackShown = React.useMemo(
    () => !!fallbackImage && isCustomVideo && (!isVideoCached || isVideoBlocked),
    [fallbackImage, isCustomVideo, isVideoCached, isVideoBlocked],
  );
  const onVideoPlayed = React.useCallback(() => setIsVideoPlayed(true), [setIsVideoPlayed]);
  React.useEffect(() => {
    if (!isCustomVideo) {
      setIsVideoBlocked(false);
      return;
    }

    isNoscriptVideoBlocked(videoUrl).then((response) => setIsVideoBlocked(response));
  }, [isCustomVideo, videoUrl]);

  return (
    <div
      ref={containerRef}
      data-testid="qa-video-background"
      className={clsx(classes.bgVideoContainer, className)}
    >
      <div data-testid="qa-bg-video-component" className={bgVideoClassName}>
        <SixteenNineIcon className={sixteenNineClassName} />
        <div className={classes.overlay} />
        {isFallbackShown && (
          <Image
            image={fallbackImage}
            base="small"
            type="background"
            size="cover"
            roundedCorners={false}
          />
        )}
        {!isVideoBlocked && (
          <VisibilitySensor
            onChange={onChange}
            delayedCall
            partialVisibility
            scrollCheck
            intervalCheck
          >
            <VideoVariant
              classes={classes}
              videoUrl={videoUrl}
              isVisible={isVisible}
              onVideoPlayed={onVideoPlayed}
            />
          </VisibilitySensor>
        )}
      </div>
    </div>
  );
};

export default memo(
  VideoBackgroundComponent,
  ({ videoUrl: prevVideoUrl }: any, { videoUrl: nextVideoUrl }: any) =>
    prevVideoUrl === nextVideoUrl,
);
